import React, { useState, useEffect, useMemo, useContext } from 'react';
import { getLongPollSwishPaymentResult, getSwishQrCodeImageUrl } from '../../../../../backend-api';
import { Button, ButtonLink } from '../../../../../components';
import { useDispatch } from 'react-redux';
import wait from '../../../../../utilities/wait';
import { failureRedCircleIcon, successGreenCircleIcon } from '../../../../../components/assets';
import SwishLogoBanner from '../../components/swish-logo-banner';
import PaymentSuccessInstructions from '../../components/payment-success-instructions';
import { t } from '../../../../../i18n';
import UrlTo from '../../../../../url-to';
import { loadCoursesCheckoutInformation } from '../../redux/actions';
import { loadGiftCardCheckoutInformation } from '../../../gift-card-checkout/redux/actions';
import PlaceholderImage from './placeholder-image';
import CourseCheckoutError from '../../course-checkout-error';
import CheckoutContext from '../../checkout-context';
import SwishPaymentStatus from '../../swish-payment-status';
import './styles.scss';


function getErrorMessage(paymentResult) {
  if (paymentResult.status === SwishPaymentStatus.DECLINED) {
    return 'DIGITAL_COURSES.CHECKOUT.ERROR.PAYMENT_DECLINED';
  }
  switch (paymentResult.errorCode) {
    case CourseCheckoutError.SWISH_TRANSACTION_DECLINED:
      return 'DIGITAL_COURSES.CHECKOUT.ERROR.SWISH_TRANSACTION_DECLINED';
    case CourseCheckoutError.SWISH_BANKID_CANCELED:
      return 'DIGITAL_COURSES.CHECKOUT.ERROR.SWISH_BANKID_CANCELED';
    case CourseCheckoutError.SWISH_PAYMENT_REQUEST_EXPIRED:
      return 'DIGITAL_COURSES.CHECKOUT.ERROR.SWISH_PAYMENT_REQUEST_EXPIRED';
    case CourseCheckoutError.SWISH_BANK_SYSTEM_PROCESSING_ERROR:
    case CourseCheckoutError.SWISH_TIMED_OUT_WAITING_FOR_BANK:
      return 'DIGITAL_COURSES.CHECKOUT.ERROR.SWISH_BANK_ERROR';
    default:
      return 'SERVER_ERROR';
  }
}

const SwishPaymentResultScreen = ({ match, location, history }) => {
  const courseId = match.params.id;
  const [paymentResult, setPaymentResult] = useState(null);
  const dispatch = useDispatch();
  const { isCoursePayment } = useContext(CheckoutContext);
  const { paymentId, email, name } = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return {
      paymentId: queryParams.get('paymentId'),
      email: queryParams.get('email'),
      name: queryParams.get('name'),
    };
  }, []);

  useEffect(() => {
    let abortController = new AbortController();

    async function longPollResult() {
      while (!abortController.signal.aborted) {
        try {
          const result = await getLongPollSwishPaymentResult(paymentId, abortController.signal);
          setPaymentResult(result);
          return;
        } catch (e) {
          await wait(5 * 1000);
        }
      }
    }
    longPollResult();

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const loadResultAction = isCoursePayment
      ? loadCoursesCheckoutInformation(courseId, { email, currency: 'sek' })
      : loadGiftCardCheckoutInformation({ currency: 'sek' });
    dispatch(loadResultAction);
  }, []);

  const isWaiting = !paymentResult;
  const isSuccess = paymentResult?.status === SwishPaymentStatus.PAID;
  const isFailed = !isWaiting && !isSuccess;

  return (
    <div className="courses-swish-checkout-result">
      <SwishLogoBanner />
      <div className='courses-swish-checkout-result__form'>
        {isWaiting && (
          <>
            <h1>{t('DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.WAITING_SWISH.TITLE')}</h1>
            <PlaceholderImage
              src={getSwishQrCodeImageUrl(paymentId)}
              className="courses-swish-checkout-result__qr"
              alt="Payment QR code"
            />
            <p>{t('DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.WAITING_SWISH.SCAN_QR')}</p>
          </>
        )}
        {isSuccess && (
          <>
            <h1>{t('DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.SUCCESS.TITLE')}</h1>
            <p>{t('DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.SUCCESS.BODY')}</p>
            <img src={successGreenCircleIcon} alt="Success" />
            {isCoursePayment && <PaymentSuccessInstructions />}
            <ButtonLink
              text={t('OK')}
              href={isCoursePayment ? UrlTo.coursesSpecificCourse(courseId) : UrlTo.coursesHome()}
              className="courses-swish-checkout-result__button"
            />
          </>
        )}
        {isFailed && (
          <>
            <h1>{t('DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.FAILURE.TITLE')}</h1>
            <p>{t('DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.FAILURE.BODY')}</p>
            <p>{t(getErrorMessage(paymentResult))}</p>
            <img src={failureRedCircleIcon} alt="Failure" />
            <Button
              text={t('TRY_AGAIN')}
              onClick={() => {
                if (history.length < 1) {
                  // After Swish callback history may be cleared
                  const previousPath = isCoursePayment
                    ? UrlTo.coursesCheckout(courseId, email, name)
                    : UrlTo.coursesGiftCardCheckout({ email, name });
                  history.push(previousPath);
                  return;
                }
                history.goBack();
              }}
              className="courses-swish-checkout-result__button"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SwishPaymentResultScreen;
